import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw, Crown, User, MapPin, LogOut, ChevronDown, Package } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface HeaderProps {
  onRefresh: () => void;
  totalProducts: number;
  currentIndex: number;
}

export default function Header({ 
  onRefresh, 
  totalProducts, 
  currentIndex
}: HeaderProps) {
  const { user, isVipUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const isVip = isVipUser();
  
  // Get real-time swipe counts from user profile
  const dailySwipesUsed = user?.profile?.daily_swipes || 0;

  // Calculate daily swipe progress (how much of the limit has been used)
  const DAILY_SWIPE_LIMIT = 10;
  const dailySwipeProgress = Math.min((dailySwipesUsed / DAILY_SWIPE_LIMIT) * 100, 100);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  /** Mobile and desktop each need their own ref — React only keeps one ref={}; the second was stealing the ref from mobile. */
  const profileMenuRefMobile = useRef<HTMLDivElement>(null);
  const profileMenuRefDesktop = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout? You will need to login again to continue swiping.')) {
      logout();
    }
  };

  const handleToggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleProfileClick = () => {
    setIsDropdownOpen(false);
    navigate('/profile');
  };

  const handleManageAddressClick = () => {
    setIsDropdownOpen(false);
    navigate('/addresses');
  };

  const handleMyOrdersClick = () => {
    setIsDropdownOpen(false);
    navigate('/profile/orders', { state: { from: '/discover' } });
  };

  const defaultShipping = user?.shipping_addresses?.find((a) => a.is_default) ?? user?.shipping_addresses?.[0];
  const addressSummary = defaultShipping
    ? `${defaultShipping.district}, ${defaultShipping.pincode}`
    : user?.profile?.address || 'No address set';

  // Close dropdown when clicking or tapping outside (both mobile + desktop roots)
  useEffect(() => {
    const handleOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      const inMobile = profileMenuRefMobile.current?.contains(target) ?? false;
      const inDesktop = profileMenuRefDesktop.current?.contains(target) ?? false;
      if (!inMobile && !inDesktop) {
        setIsDropdownOpen(false);
      }
    };
    const handleMouseDown = (e: MouseEvent) => handleOutside(e);
    const handleTouchStart = (e: TouchEvent) => handleOutside(e);

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('touchstart', handleTouchStart, { passive: true });
      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('touchstart', handleTouchStart);
      };
    }
  }, [isDropdownOpen]);

  return (
    <div className={`relative w-full px-2 sm:px-4 py-2 sm:py-3 ${isDropdownOpen ? 'z-[200]' : 'z-20'}`}>
      {/* Mobile Layout - Single Row */}
      <div className="flex items-center justify-between sm:hidden">
        {/* Left: Refresh Button */}
        <button
          onClick={onRefresh}
          className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-xl transition-all duration-200"
          title="Refresh products"
        >
          <RotateCcw size={18} />
        </button>

        {/* Center: Progress Bar or VIP Badge */}
        <div className="flex-1 flex justify-center">
          {!isVip && (
            <div className="flex items-center gap-2">
              <div className="w-24 h-3 rounded-full overflow-hidden bg-muted border border-border">
                <div
                  className={`h-full transition-all duration-500 ease-out ${
                    dailySwipeProgress >= 80
                      ? 'gradient-accent'
                      : dailySwipeProgress >= 60
                      ? 'gradient-secondary'
                      : 'gradient-primary'
                  }`}
                  style={{ width: `${dailySwipeProgress}%` }}
                />
              </div>
              <span className="text-primary font-bold text-sm">
                {dailySwipesUsed}/{DAILY_SWIPE_LIMIT}
              </span>
            </div>
          )}

          {isVip && (
            <div className="flex items-center gap-1 px-3 py-1.5 gradient-accent rounded-full text-white text-xs font-bold shadow-lg">
              <Crown size={14} />
              <span>VIP</span>
            </div>
          )}
        </div>
        
        {/* Right: Profile Dropdown (mobile) */}
        <div className="relative" ref={profileMenuRefMobile}>
          <button
            onClick={handleToggleDropdown}
            type="button"
            className="flex items-center gap-1 p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-xl transition-all duration-200 min-w-[44px] min-h-[44px] justify-center touch-manipulation"
            title="User Profile"
          >
            <User size={18} />
            <ChevronDown size={14} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Backdrop: blocks touch/click from reaching product card on mobile */}
          {isDropdownOpen && (
            <div
              className="fixed inset-0 z-[100] sm:hidden bg-black/20"
              aria-hidden="true"
              onClick={() => setIsDropdownOpen(false)}
            />
          )}
          {/* Dropdown Menu - above backdrop so menu taps register */}
          {isDropdownOpen && (
            <div className="absolute top-full right-0 mt-2 w-52 rounded-2xl border border-border bg-card text-card-foreground shadow-2xl z-[110] isolate pointer-events-auto overflow-hidden">
              {/* User Info Header */}
              <div className="p-4 text-sm border-b border-border bg-muted">
                <p className="flex items-center gap-2 font-bold text-foreground">
                  <User size={16} className="text-primary" /> {user?.username || 'Guest'}
                </p>
                <p className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  <MapPin size={14} className="text-accent" /> {addressSummary}
                </p>
              </div>

              {/* Menu Options - min touch target 44px */}
              <div className="p-2 bg-card">
                <button
                  type="button"
                  onClick={() => handleProfileClick()}
                  className="w-full text-left p-3 min-h-[44px] text-sm text-card-foreground hover:bg-primary/15 hover:text-primary rounded-xl flex items-center gap-2 transition-all duration-200 font-medium touch-manipulation"
                >
                  <User size={16} /> Profile
                </button>

                <button
                  type="button"
                  onClick={() => handleManageAddressClick()}
                  className="w-full text-left p-3 min-h-[44px] text-sm text-card-foreground hover:bg-accent/15 hover:text-accent rounded-xl flex items-center gap-2 transition-all duration-200 font-medium touch-manipulation"
                >
                  <MapPin size={16} /> Manage Address
                </button>

                <button
                  type="button"
                  onClick={() => handleMyOrdersClick()}
                  className="w-full text-left p-3 min-h-[44px] text-sm text-card-foreground hover:bg-primary/15 hover:text-primary rounded-xl flex items-center gap-2 transition-all duration-200 font-medium touch-manipulation"
                >
                  <Package size={16} /> My orders
                </button>

                <div className="w-full h-px bg-border my-2" />

                <button
                  type="button"
                  onClick={() => {
                    setIsDropdownOpen(false);
                    handleLogout();
                  }}
                  className="w-full text-left p-3 min-h-[44px] text-sm text-red-500 hover:bg-red-500/15 rounded-xl flex items-center gap-2 transition-all duration-200 font-medium touch-manipulation"
                >
                  <LogOut size={16} /> Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Layout - Same as Mobile */}
      <div className="hidden sm:flex items-center justify-between w-full">
        {/* Left: Refresh Button */}
        <button
          onClick={onRefresh}
          className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-xl transition-all duration-200"
          title="Refresh products"
        >
          <RotateCcw size={18} />
        </button>

        {/* Center: Progress Bar or VIP Badge */}
        <div className="flex-1 flex justify-center">
          {!isVip && (
            <div className="flex items-center gap-2">
              <div className="w-32 h-3 rounded-full overflow-hidden bg-muted border border-border">
                <div
                  className={`h-full transition-all duration-500 ease-out ${
                    dailySwipeProgress >= 80
                      ? 'gradient-accent'
                      : dailySwipeProgress >= 60
                      ? 'gradient-secondary'
                      : 'gradient-primary'
                  }`}
                  style={{ width: `${dailySwipeProgress}%` }}
                />
              </div>
              <span className="text-primary font-bold text-base">
                {dailySwipesUsed}/{DAILY_SWIPE_LIMIT}
              </span>
            </div>
          )}

          {isVip && (
            <div className="flex items-center gap-1.5 px-4 py-1.5 gradient-accent rounded-full text-white text-sm font-bold shadow-lg">
              <Crown size={16} />
              <span>VIP</span>
            </div>
          )}
        </div>

        {/* Right: Profile Dropdown (desktop) */}
        <div className="relative" ref={profileMenuRefDesktop}>
          <button
            onClick={handleToggleDropdown}
            type="button"
            className="flex items-center gap-1 p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-xl transition-all duration-200"
            title="User Profile"
          >
            <User size={18} />
            <ChevronDown size={14} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div
              className="absolute top-full right-0 mt-2 w-52 rounded-2xl border border-border bg-card text-card-foreground shadow-2xl z-50 overflow-hidden"
            >
              {/* User Info Header */}
              <div className="p-4 text-sm border-b border-border bg-muted">
                <p className="flex items-center gap-2 font-bold text-foreground">
                  <User size={16} className="text-primary" /> {user?.username || 'Guest'}
                </p>
                <p className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  <MapPin size={14} className="text-accent" /> {addressSummary}
                </p>
              </div>

              {/* Menu Options */}
              <div className="p-2 bg-card">
                <button
                  onClick={handleProfileClick}
                  className="w-full text-left p-3 text-sm text-card-foreground hover:bg-primary/15 hover:text-primary rounded-xl flex items-center gap-2 transition-all duration-200 font-medium"
                >
                  <User size={16} /> Profile
                </button>

                <button
                  onClick={handleManageAddressClick}
                  className="w-full text-left p-3 text-sm text-card-foreground hover:bg-accent/15 hover:text-accent rounded-xl flex items-center gap-2 transition-all duration-200 font-medium"
                >
                  <MapPin size={16} /> Manage Address
                </button>

                <button
                  onClick={handleMyOrdersClick}
                  className="w-full text-left p-3 text-sm text-card-foreground hover:bg-primary/15 hover:text-primary rounded-xl flex items-center gap-2 transition-all duration-200 font-medium"
                >
                  <Package size={16} /> My orders
                </button>

                <div className="w-full h-px bg-border my-2" />

                <button
                  onClick={() => {
                    setIsDropdownOpen(false);
                    handleLogout();
                  }}
                  className="w-full text-left p-3 text-sm text-red-500 hover:bg-red-500/15 rounded-xl flex items-center gap-2 transition-all duration-200 font-medium"
                >
                  <LogOut size={16} /> Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}